/**
 * ViewRequirements.jsx — Bidder views and filters matched requirements.
 *
 * Filters: category, location, budget range, timeline, experience level,
 *          certification, engagement type, sort order
 *
 * Requirements: 23.4
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import FilterBar from '../../components/FilterBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';
import { buildProposalMediaUrl } from '../../utils/mediaUrls';

const CATEGORIES = [
  'Penetration Testing', 'Vulnerability Assessment', 'Security Audit',
  'Incident Response', 'Cloud Security', 'Network Security',
  'Application Security', 'Compliance & Risk',
];

const EXPERIENCE_LEVELS = [
  { value: 'junior', label: 'Junior (0–3 yrs)' },
  { value: 'mid',    label: 'Mid-level (3–7 yrs)' },
  { value: 'senior', label: 'Senior (7+ yrs)' },
];

const CERTIFICATIONS = ['CISSP', 'CEH', 'OSCP', 'CISM', 'ISO27001', 'PCI-DSS', 'SOC2'];

const ENGAGEMENT_TYPES = [
  { value: 'MULTI_BID',  label: 'Multi Bid' },
  { value: 'ONE_TO_ONE', label: 'One to One' },
];

const SORT_OPTIONS = [
  { value: 'newest',        label: 'Newest first' },
  { value: 'amount_asc',    label: 'Budget: Low → High' },
  { value: 'amount_desc',   label: 'Budget: High → Low' },
  { value: 'timeline_asc',  label: 'Timeline: Soonest first' },
  { value: 'timeline_desc', label: 'Timeline: Latest first' },
];

const FILTER_CONFIG = [
  { key: 'sort',            type: 'select', label: 'Sort by',          options: SORT_OPTIONS },
  { key: 'category',        type: 'select', label: 'Category',         options: CATEGORIES.map((c) => ({ value: c, label: c })) },
  { key: 'geo',             type: 'text',   label: 'Location',         placeholder: 'e.g. India, Remote' },
  { key: 'experience',      type: 'select', label: 'Experience Level', options: EXPERIENCE_LEVELS },
  { key: 'certification',   type: 'select', label: 'Certification',    options: CERTIFICATIONS.map((c) => ({ value: c, label: c })) },
  { key: 'engagement_type', type: 'select', label: 'Engagement Type',  options: ENGAGEMENT_TYPES },
  {
    key: 'budget_range',
    type: 'range_slider',
    label: 'Budget Range (Rs. Lakhs)',
    min: 0, max: 100, step: 1,
    format: (v) => `Rs.${v}L`,
  },
  { key: 'timeline_max', type: 'number', label: 'Max Timeline (weeks)', min: 1, max: 52, placeholder: 'e.g. 8' },
];

const DEFAULT_FILTERS = {
  sort: 'newest',
  category: '',
  geo: '',
  experience: '',
  certification: '',
  engagement_type: '',
  budget_range: [0, 100],
  timeline_max: '',
};

function formatTimeline(req) {
  const weeks = Number(req.timeline_weeks);
  if (Number.isFinite(weeks) && weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''}`;
  return req.timeline_end || '—';
}

export default function ViewRequirements() {
  const navigate = useNavigate();
  const location = useLocation();
  const [allRequirements, setAllRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    setLoading(true);
    setError('');
    apiClient
      .get('/api/v1/requirements/list/')
      .then(({ data }) => setAllRequirements(Array.isArray(data) ? data : data.results ?? []))
      .catch((err) => {
        const status = err.response?.status;
        const msg = err.response?.data?.error?.message;
        if (status === 401) {
          setError('Your session expired. Please log in again.');
        } else if (msg) {
          setError(`Error: ${msg}`);
        } else if (status) {
          setError(`Failed to load requirements (HTTP ${status}). Check the backend logs.`);
        } else {
          setError('Cannot reach the server. Make sure the backend is running.');
        }
      })
      .finally(() => setLoading(false));
  }, [location.key]);

  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const handleReset = () => setFilters(DEFAULT_FILTERS);

  // Client-side filter + sort
  const filtered = useMemo(() => {
    let reqs = [...allRequirements];

    if (filters.category) reqs = reqs.filter((r) => r.category === filters.category);
    if (filters.geo) reqs = reqs.filter((r) => r.geo_preference?.toLowerCase().includes(filters.geo.toLowerCase()));
    if (filters.experience) reqs = reqs.filter((r) => r.experience_level === filters.experience);
    if (filters.certification) reqs = reqs.filter((r) => r.required_certifications?.includes(filters.certification));
    if (filters.engagement_type) reqs = reqs.filter((r) => r.engagement_type === filters.engagement_type);
    if (filters.timeline_max) {
      const maxWeeks = Number(filters.timeline_max);
      if (!isNaN(maxWeeks)) reqs = reqs.filter((r) => Number(r.timeline_weeks) <= maxWeeks);
    }

    const [bMin, bMax] = filters.budget_range || [0, 100];
    const bMinRupees = bMin * 100000;
    const bMaxRupees = bMax * 100000;
    if (bMin > 0 || bMax < 100) {
      reqs = reqs.filter((r) => {
        const max = Number(r.budget_max);
        return max >= bMinRupees && max <= bMaxRupees;
      });
    }

    // Sort
    switch (filters.sort) {
      case 'amount_asc':    reqs.sort((a, b) => Number(a.budget_max) - Number(b.budget_max)); break;
      case 'amount_desc':   reqs.sort((a, b) => Number(b.budget_max) - Number(a.budget_max)); break;
      case 'timeline_asc':  reqs.sort((a, b) => new Date(a.timeline_end) - new Date(b.timeline_end)); break;
      case 'timeline_desc': reqs.sort((a, b) => new Date(b.timeline_end) - new Date(a.timeline_end)); break;
      default:              reqs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return reqs;
  }, [allRequirements, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.sort !== 'newest') count++;
    if (filters.category) count++;
    if (filters.geo) count++;
    if (filters.experience) count++;
    if (filters.certification) count++;
    if (filters.engagement_type) count++;
    if (filters.timeline_max) count++;
    const [bMin, bMax] = filters.budget_range || [0, 100];
    if (bMin > 0 || bMax < 100) count++;
    return count;
  }, [filters]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, p: 3 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{
          mb: 3,
          textTransform: 'none',
          borderColor: 'rgba(255,255,255,0.12)',
          color: '#cbd5e1',
          '&:hover': {
            borderColor: '#818cf8',
            bgcolor: 'rgba(129, 140, 248, 0.04)',
          }
        }}
      >
        Back to Home
      </Button>
      <Typography variant="h4" gutterBottom>Matched Requirements</Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {allRequirements.length > 0 && (
        <>
          <FilterBar
            config={FILTER_CONFIG}
            values={filters}
            onChange={handleFilterChange}
            onReset={handleReset}
            activeCount={activeFilterCount}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filtered.length} of {allRequirements.length} requirement{allRequirements.length !== 1 ? 's' : ''}
          </Typography>
        </>
      )}

      {allRequirements.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.01)', borderStyle: 'dashed', borderWidth: '1.5px', borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: '16px', mt: 3 }}>
          <Box sx={{ color: 'secondary.light', mb: 2.5, display: 'flex', justifyContent: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>No Matched Requirements</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', lineHeight: 1.65 }}>
            There are no open requirements matched to your profile right now. Strengthening your bidder profile (adding locations, updating certifications, or adjusting availability) will boost your match scoring instantly.
          </Typography>
        </Paper>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary">No requirements match the current filters.</Typography>
      ) : (
        filtered.map((req) => {
          const anonymizedDocUrl = req.scope_doc_s3_key
            ? buildProposalMediaUrl(req.scope_doc_s3_key)
            : null;

          return (
            <Paper key={req.id} sx={{ p: 2, mb: 2 }} elevation={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">{req.category}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip label={req.status} size="small" />
                {req.engagement_type && <Chip label={req.engagement_type} size="small" variant="outlined" />}
                {req.experience_level && <Chip label={req.experience_level} size="small" variant="outlined" color="info" />}
              </Box>
            </Box>

              <Divider sx={{ my: 1 }} />

            <Typography variant="body2">
              <strong>Budget:</strong>{' '}
              {req.budget_min != null && req.budget_min !== ''
                ? `Rs.${Number(req.budget_min).toLocaleString('en-IN')} – `
                : 'Up to '}
              Rs.{Number(req.budget_max).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="body2">
              <strong>Location:</strong> {req.geo_preference}
            </Typography>
            <Typography variant="body2">
              <strong>Timeline:</strong> {formatTimeline(req)}
            </Typography>

              {req.required_certifications?.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {req.required_certifications.map((cert) => (
                    <Chip key={cert} label={cert} size="small" variant="outlined" />
                  ))}
                </Box>
              )}

              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {anonymizedDocUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    href={anonymizedDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View anonymized document for requirement ${req.id}`}
                  >
                    View Anonymized PDF
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(`/bidder/requirements/${req.id}/bid`)}
                  aria-label={`Submit bid for requirement ${req.id}`}
                >
                  Submit Bid
                </Button>
              </Box>
            </Paper>
          );
        })
      )}
    </Box>
  );
}
